/**
 * ClassDetailPage - View class details and manage sessions within a class
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { classesService } from '@/services/classes';
import { sessionsService } from '@/services/sessions';
import { ClassWithMetrics, UpdateClassRequest } from '@/types/class';
import { SessionWithPlayers, SessionStatus } from '@/types/session';
import { CreateClassModal } from '@/components/CreateClassModal';
import { Button } from '@/components/ui/button';

export function ClassDetailPage() {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const [classData, setClassData] = useState<ClassWithMetrics | null>(null);
  const [sessions, setSessions] = useState<SessionWithPlayers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (classId) {
      loadClassData();
    }
  }, [classId]);

  async function loadClassData() {
    if (!classId) return;

    try {
      setLoading(true);
      setError(null);

      const [classResponse, sessionsResponse] = await Promise.all([
        classesService.getClass(classId),
        sessionsService.listSessions(classId),
      ]);

      setClassData(classResponse);
      setSessions(sessionsResponse.sessions);
    } catch (err: any) {
      console.error('Failed to load class data:', err);
      setError(err.message || 'Failed to load class data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateClass(data: UpdateClassRequest) {
    if (!classId) return;
    await classesService.updateClass(classId, data);
    await loadClassData();
  }

  async function handleDeleteClass() {
    if (!classId || !classData) return;

    if (classData.sessionCount > 0) {
      alert('Cannot delete class with sessions. Move or delete sessions first.');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${classData.className}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await classesService.deleteClass(classId);
      navigate('/login/teacher');
    } catch (err: any) {
      alert(err.message || 'Failed to delete class');
    } finally {
      setIsDeleting(false);
    }
  }

  function getStatusBadgeClass(status: SessionStatus): string {
    switch (status) {
      case SessionStatus.WAITING:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#DD6832]/10 text-[#DD6832]';
      case SessionStatus.LIVE:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#367A25]/10 text-[#367A25]';
      case SessionStatus.COMPLETE:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#E2E5EC] text-[#12356B]';
      default:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#E2E5EC] text-[#12356B]';
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#12356B] mb-4" />
          <p className="text-slate-600">Loading class...</p>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 mb-4">
            <strong>Error:</strong> {error || 'Class not found'}
          </div>
          <Button
            onClick={() => navigate('/login/teacher')}
            className="bg-[#367A25] text-white hover:bg-[#2d6820]"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const activeSessions = sessions.filter((s) => s.status !== SessionStatus.COMPLETE);
  const completedSessions = sessions.filter((s) => s.status === SessionStatus.COMPLETE);

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-slate-600 mb-4">
          <Link to="/login/teacher" className="hover:text-[#12356B]">
            Dashboard
          </Link>
          <span className="mx-2">/</span>
          <span className="text-[#12356B] font-medium">{classData.className}</span>
        </nav>

        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-[#12356B]">{classData.className}</h1>
              {classData.color && (
                <div
                  className="w-6 h-6 rounded-full border-2 border-[#CED4DE]"
                  style={{ backgroundColor: classData.color }}
                />
              )}
            </div>
            {classData.description && (
              <p className="text-slate-600 mb-4">{classData.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(true)}
              className="border-[#12356B] text-[#12356B] hover:bg-[#E2E5EC] hover:text-[#12356B]"
            >
              Edit Class
            </Button>
            <Button
              variant="outline"
              onClick={handleDeleteClass}
              disabled={isDeleting || classData.sessionCount > 0}
              className={`border-[#DD6832] text-[#DD6832] hover:bg-[#DD6832]/10 hover:text-[#DD6832] ${
                classData.sessionCount > 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={classData.sessionCount > 0 ? 'Cannot delete class with sessions' : 'Delete class'}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              onClick={() => navigate('/login/teacher/new')}
              className="bg-[#367A25] text-white hover:bg-[#2d6820]"
            >
              Create Session
            </Button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">Total Sessions</h3>
            <p className="text-4xl font-bold text-[#12356B] my-2">{classData.sessionCount}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">Active</h3>
            <p className="text-4xl font-bold text-[#DD6832] my-2">{activeSessions.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">Completed</h3>
            <p className="text-4xl font-bold text-[#367A25] my-2">{completedSessions.length}</p>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[#CED4DE]">
            <h2 className="text-xl font-bold text-[#12356B]">Sessions in this Class</h2>
          </div>

          {sessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-2">No sessions yet</p>
              <p className="text-slate-500 mb-4">Create your first session in this class!</p>
              <Button
                onClick={() => navigate('/login/teacher/new')}
                className="bg-[#367A25] text-white hover:bg-[#2d6820]"
              >
                Create Session
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#E2E5EC]/60 border-b border-[#CED4DE]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Players</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E5EC]">
                {sessions.map((session) => (
                  <tr key={session.sessionId} className="hover:bg-[#E2E5EC]/35">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded font-mono text-sm font-semibold tracking-[0.08em]">
                        {session.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {session.sessionName || <span className="text-slate-400 italic">Unnamed</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadgeClass(session.status)}>{session.status}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {session.joinedCount}/{session.requiredPlayers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(session.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/login/teacher/sessions/${session.sessionId}`)}
                        className="px-3 py-1 bg-[#E2E5EC] text-[#12356B] rounded hover:bg-[#CED4DE] transition-colors text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit Class Modal */}
      <CreateClassModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateClass}
        initialData={classData}
      />
    </div>
  );
}
